import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { ICE, ICEPagingRes } from '@/shared/interfaces/erp';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchICEAll = (params?: getICEParams, enabled = true) => {
  return useQuery({
    queryKey: ['ICE', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getICEAll(params);
    },
    enabled,
  });
};

export const useGetICE = (id: string) => {
  return useQuery({
    queryKey: ['ICE', id],
    queryFn: async () => {
      return getICE(id);
    },
  });
};

export const useCreateICE = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createICE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ICE'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'ice creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateICE = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateICE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ICE'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'ice actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveICE = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeICE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ICE'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getICEParams = Partial<ICE> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateICEParams = Omit<ICE, 'id_ice'>;
export type UpdateICEParams = {
  id: string;
  data: CreateICEParams;
};

export const getICEAll = async (params?: getICEParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<ICEPagingRes>(`${urlBackend}api/v1/ice/?${queryParams}`, true);
};

export const getICE = async (id: string) => {
  try {
    return await get<ICE>(`${urlBackend}api/v1/ice/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createICE = async (data: CreateICEParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<ICE>(`${urlBackend}api/v1/ice/`, data, true);
};

export const updateICE = async ({ id, data }: UpdateICEParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<ICE>(`${urlBackend}api/v1/ice/${id}/`, data, true);
};

export const removeICE = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<ICE>(`${urlBackend}api/v1/ice/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};